import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";
import {
  MAX_post_image_SIZE,
  MAX_post_video_SIZE,
} from "./../../shared/constant";
import { Crop } from "../../shared/crop";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import EasyCropper from "react-easy-crop";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import "react-toastify/dist/ReactToastify.css";

const CreatePostPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    caption: "",
    media: null,
    thumbnail: null,
    thumbnailPreview: null,
    mediaPreview: null,
  });
  const [showMediaCrop, setShowMediaCrop] = useState(false);
  const [showVideoCrop, setShowVideoCrop] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState("");
  const [cropVideoSrc, setCropVideoSrc] = useState("");
  const cropperRef = useRef(null);
  
  // Video crop state
  const [videoCrop, setVideoCrop] = useState({ x: 0, y: 0 });
  const [videoZoom, setVideoZoom] = useState(1);
  const [videoCropArea, setVideoCropArea] = useState(null);

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const removeFile = (field) => {
    setFormData((prev) => {
      if (field === "thumbnail" && prev.thumbnailPreview) {
        URL.revokeObjectURL(prev.thumbnailPreview);
      }
      if (field === "media" && prev.mediaPreview) {
        URL.revokeObjectURL(prev.mediaPreview);
      }
      return {
        ...prev,
        [field]: null,
        thumbnailPreview: field === "thumbnail" ? null : prev.thumbnailPreview,
        mediaPreview: field === "media" ? null : prev.mediaPreview,
      };
    });
  };

  const handleThumbnailCrop = (cropData) => {
    if (cropData && cropData.fileCropped && cropData.originalFile) {
      const croppedFile = new File(
        [cropData.fileCropped],
        cropData.originalFile.name,
        {
          type: cropData.originalFile.type,
          lastModified: Date.now(),
        }
      );
      const maxSize = MAX_post_image_SIZE * 1024 * 1024;
      if (croppedFile.size > maxSize) {
        toast.error(
          `Thumbnail size should be less than ${MAX_post_image_SIZE}MB`
        );
        return;
      }

      setFormData((prev) => ({
        ...prev,
        thumbnail: croppedFile,
        thumbnailPreview: URL.createObjectURL(croppedFile),
      }));
    }
  };

  const handleMediaCrop = () => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const cropper = cropperRef.current.cropper;
      const dataURL = cropper.getCroppedCanvas().toDataURL();
      
      if (!dataURL) {
        toast.error("Failed to process image. Please try again.");
        return;
      }
      fetch(dataURL)
        .then(res => res.blob())
        .then(blob => {
          const croppedFile = new File(
            [blob],
            pendingMediaFile.name,
            {
              type: pendingMediaFile.type,
              lastModified: Date.now(),
            }
          );
          
          const maxSize = MAX_post_image_SIZE * 1024 * 1024;
          if (croppedFile.size > maxSize) {
            toast.error(
              `Media size should be less than ${MAX_post_image_SIZE}MB`
            );
            return;
          }

          setFormData((prev) => ({
            ...prev,
            media: croppedFile,
            mediaPreview: URL.createObjectURL(croppedFile),
          }));
          setShowMediaCrop(false);
          setPendingMediaFile(null);
          setCropImageSrc("");
        })
        .catch(error => {
          console.error("Error processing cropped image:", error);
          toast.error("Failed to process cropped image. Please try again.");
        });
    }
  };

  const handleVideoCrop = () => {
    if (pendingVideoFile) {
      // For now, we'll use the original video file since react-easy-crop doesn't support video cropping
      // In a real implementation, you would need a video processing library
      const croppedFile = new File(
        [pendingVideoFile],
        pendingVideoFile.name,
        {
          type: pendingVideoFile.type,
          lastModified: Date.now(),
        }
      );
      
      const maxSize = MAX_post_video_SIZE * 1024 * 1024;
      if (croppedFile.size > maxSize) {
        toast.error(
          `Video size should be less than ${MAX_post_video_SIZE}MB`
        );
        return;
      }

      // Create proper video URL
      const videoUrl = URL.createObjectURL(croppedFile);
      
      setFormData((prev) => ({
        ...prev,
        media: croppedFile,
        mediaPreview: videoUrl,
      }));
      setShowVideoCrop(false);
      setPendingVideoFile(null);
      setCropVideoSrc("");
      setVideoCrop({ x: 0, y: 0 });
      setVideoZoom(1);
      setVideoCropArea(null);
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!formData.caption.trim()) {
      toast.error("Please enter a caption for your post");
      return;
    }
    if (!formData.media && !formData.thumbnail) {
      toast.error("Please upload either a media file or thumbnail");
      return;
    }
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("caption", formData.caption);
      if (formData.media) {
        formDataToSend.append("media", formData.media);
      }
      if (formData.thumbnail) {
        formDataToSend.append("thumbnail", formData.thumbnail);
      }
      await sellerService.createPost(formDataToSend);
      toast.success("Post created successfully!");
      navigate(`/social/posts/`);
    } catch (error) {
      let message = "Failed to creating post. Please try again.";
      if (error.response && error.response.data) {
        if (
          error.response.data.non_field_errors &&
          error.response.data.non_field_errors.length > 0
        ) {
          message = error.response.data.non_field_errors[0];
        }
      }
      toast.error(message);
      console.error("Error creating post:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/social/posts");
  };

  const resetForm = () => {
    if (formData.thumbnailPreview) {
      URL.revokeObjectURL(formData.thumbnailPreview);
    }
    if (formData.mediaPreview) {
      URL.revokeObjectURL(formData.mediaPreview);
    }
    setFormData({
      caption: "",
      media: null,
      thumbnail: null,
      thumbnailPreview: null,
      mediaPreview: null,
    });
    setShowMediaCrop(false);
    setShowVideoCrop(false);
    setPendingMediaFile(null);
    setPendingVideoFile(null);
    setCropImageSrc("");
    setCropVideoSrc("");
    setVideoCrop({ x: 0, y: 0 });
    setVideoZoom(1);
    setVideoCropArea(null);
  };

  useEffect(() => {
    return () => {
      if (formData.thumbnailPreview) {
        URL.revokeObjectURL(formData.thumbnailPreview);
      }
      if (formData.mediaPreview) {
        URL.revokeObjectURL(formData.mediaPreview);
      }
    };
  }, [formData.thumbnailPreview, formData.mediaPreview]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Create Post</h4>
            </div>
            <div className="card-body">
              <form onSubmit={createPost}>
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Caption <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="3"
                        maxLength={500}
                        value={formData.caption}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            caption: e.target.value,
                          }))
                        }
                        required
                      />
                      <div className="form-text">
                        Write a compelling caption to engage your audience
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5
                          className="text-dark mb-0"
                          style={{ color: "#6f42c1" }}
                        >
                          Upload Media Files
                        </h5>
                      </div>
                      <div className="card-body">
                        <hr
                          className="mb-3"
                          style={{ borderColor: "#e9ecef" }}
                        />
                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold">
                            Media File <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <input
                              type="file"
                              className="form-control"
                              id="media-file"
                              accept="image/*,video/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const validImageTypes = [
                                    "image/jpeg",
                                    "image/jpg",
                                    "image/png",
                                    "image/gif",
                                    "image/webp",
                                  ];
                                  const validVideoTypes = [
                                    "video/mp4",
                                    "video/mov",
                                  ];
                                  const isValidType = [
                                    ...validImageTypes,
                                    ...validVideoTypes,
                                  ].includes(file.type);
                                  if (!isValidType) {
                                    toast.error(
                                      "Please select a valid image or video file"
                                    );
                                    e.target.value = "";
                                    return;
                                  }
                                  const maxSize = file.type.startsWith("video/")
                                    ? MAX_post_video_SIZE * 1024 * 1024
                                    : MAX_post_image_SIZE * 1024 * 1024;
                                  if (file.size > maxSize) {
                                    const maxSizeMB = file.type.startsWith(
                                      "video/"
                                    )
                                      ? `${MAX_post_video_SIZE}MB`
                                      : `${MAX_post_image_SIZE}MB`;
                                    toast.error(
                                      `File size should be less than ${maxSizeMB}`
                                    );
                                    e.target.value = "";
                                    return;
                                  }
                                  if (validImageTypes.includes(file.type)) {
                                    setPendingMediaFile(file);
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                      setCropImageSrc(reader.result);
                                      setShowMediaCrop(true);
                                    };
                                    reader.readAsDataURL(file);
                                  } else if (validVideoTypes.includes(file.type)) {
                                    setPendingVideoFile(file);
                                    // Create proper video URL for cropping
                                    const videoUrl = URL.createObjectURL(file);
                                    setCropVideoSrc(videoUrl);
                                    setShowVideoCrop(true);
                                  } else {
                                    handleFileChange(e, "media");
                                  }
                                }
                              }}
                              required={!formData.thumbnail}
                            />
                          </div>
                          <small className="form-text text-muted">
                            Supported formats: Images ('jpg', 'jpeg', 'png', 'webp', 'gif') and Videos
                            (MP4, MOV) - Max size:{MAX_post_image_SIZE}MB for
                            images,{MAX_post_video_SIZE}MB for videos
                          </small>
                          {formData.media && (
                            <div className="mt-2">
                              {formData.mediaPreview && (
                                <div className="mb-2">
                                  {formData.media.type.startsWith("video/") ? (
                                    <video
                                      src={formData.mediaPreview}
                                      controls
                                      preload="metadata"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "200px",
                                        borderRadius: "8px",
                                        objectFit: "cover"
                                      }}
                                    >
                                      Your browser does not support the video tag.
                                    </video>
                                  ) : (
                                    <img
                                      src={formData.mediaPreview}
                                      alt="Media preview"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "200px",
                                        borderRadius: "8px",
                                        objectFit: "cover"
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                              <small className="text-success mb-2 d-block">
                                <i className="fas fa-check-circle me-1"></i>
                                Media ready for upload: {formData.media.name}
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeFile("media")}
                              >
                                <i className="feather icon-x"></i> Remove Media
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold">
                            Thumbnail Image
                          </label>
                          <div className="d-flex flex-column align-items-center">
                            <Crop
                              cropShape="rect"
                              onChangeCropper={handleThumbnailCrop}
                              maxSizeMib={MAX_post_image_SIZE}
                              height={200}
                              width={250}
                              aspectRatio={1}
                              imagePreview={formData.thumbnailPreview}
                              isButtonHidden={false}
                              guid="thumbnail-crop"
                              buttonBackGroundColor="#f8f9fa"
                            />
                            <small className="form-text text-muted mt-2 text-center">
                              Optional: Custom thumbnail image ('jpg', 'jpeg', 'png', 'webp', 'gif') -
                              Max size: {MAX_post_image_SIZE}MB
                            </small>
                            {formData.thumbnail && (
                              <div className="mt-2">
                                <small className="text-success mb-2 d-block">
                                  <i className="fas fa-check-circle me-1"></i>
                                  Thumbnail ready for upload:{" "}
                                  {formData.thumbnail.name}
                                </small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeFile("thumbnail")}
                                >
                                  <i className="feather icon-x"></i> Remove
                                  Thumbnail
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                    <Modal
                    size="lg"
                    backdrop="static"
                    keyboard={false}
                    show={showMediaCrop}
                    onHide={() => {
                      setShowMediaCrop(false);
                      setPendingMediaFile(null);
                      setCropImageSrc("");
                    }}
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Crop Media Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div className="d-flex justify-content-center">
                        <Cropper
                          aspectRatio={1}
                          src={cropImageSrc}
                          zoomable={false}
                          minCropBoxHeight={50}
                          minCropBoxWidth={50}
                          background={false}
                          responsive={true}
                          cropBoxResizable={true}
                          checkOrientation={false}
                          guides={true}
                          viewMode={1}
                          ref={cropperRef}
                          style={{ height: 400, width: "100%" }}
                        />
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setShowMediaCrop(false);
                          setPendingMediaFile(null);
                          setCropImageSrc("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleMediaCrop}
                      >
                        Crop & Apply
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  {/* Video Crop Modal */}
                  <Modal
                    size="lg"
                    backdrop="static"
                    keyboard={false}
                    show={showVideoCrop}
                    onHide={() => {
                      setShowVideoCrop(false);
                      setPendingVideoFile(null);
                      setCropVideoSrc("");
                      setVideoCrop({ x: 0, y: 0 });
                      setVideoZoom(1);
                      setVideoCropArea(null);
                    }}
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Crop Video</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div className="crop-container" style={{ position: 'relative', height: '400px', width: '100%', background: '#000' }}>
                        <EasyCropper
                          video={cropVideoSrc}
                          crop={videoCrop}
                          zoom={videoZoom}
                          aspect={1}
                          onCropChange={setVideoCrop}
                          onZoomChange={setVideoZoom}
                          onCropComplete={(croppedArea, croppedAreaPixels) => {
                            setVideoCropArea(croppedAreaPixels);
                          }}
                          onVideoLoaded={(res) => {
                            console.log('Video loaded:', res);
                          }}
                        />
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setShowVideoCrop(false);
                          setPendingVideoFile(null);
                          setCropVideoSrc("");
                          setVideoCrop({ x: 0, y: 0 });
                          setVideoZoom(1);
                          setVideoCropArea(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleVideoCrop}
                      >
                        Apply Crop
                      </Button>
                    </Modal.Footer>
                  </Modal>
                  
                  <div className="col-md-12">
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={
                          loading ||
                          !formData.caption.trim() ||
                          (!formData.media && !formData.thumbnail)
                        }
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating Post...
                          </>
                        ) : (
                          <>
                            <i className="feather icon-plus"></i> Create Post
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={resetForm}
                        disabled={loading}
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePostPage;
